import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store";

export interface System {
  _id: string;
  name: string;
  code?: "MATRIC" | "OLEVEL" | "AKU" | "COLLEGE";
  style?: "local" | "cambridge";
}

const initialState: { systems: System[] } = {
  systems: [],
};

const systemsSlice = createSlice({
  name: "systems",
  initialState,
  reducers: {
    setSystems: (state, action: PayloadAction<System[]>) => {
      return { ...state, systems: action.payload };
    },
  },
});

export const { setSystems } = systemsSlice.actions;

// Fixed display/selection priority: Matric, O-Level, AKU, College. Every
// System dropdown in the app should list them in this order, and any page
// that defaults its selection to `systems[0]` gets the correct default for
// free since the underlying array is already sorted this way.
const SYSTEM_PRIORITY: Record<string, number> = { MATRIC: 1, OLEVEL: 2, AKU: 3, COLLEGE: 4 };
const byPriority = (systems: System[]) =>
  [...systems].sort((a, b) => (SYSTEM_PRIORITY[a.code || ""] ?? 99) - (SYSTEM_PRIORITY[b.code || ""] ?? 99));

export const selectSystems = (state: RootState) => byPriority(state.systems.systems || []);

// Systems restricted to the currently-selected campus (Branch.systems).
// Falls back to the full catalog if that campus hasn't been assigned any
// systems yet, so the dropdown is never empty during rollout.
export const selectSystemsForCurrentBranch = (state: RootState) => {
  const systems = state.systems.systems || [];
  const allowedIds = state.branches.currentBranch?.systems?.map((s) => s._id);
  const filtered = allowedIds?.length ? systems.filter((s) => allowedIds.includes(s._id)) : systems;
  return byPriority(filtered);
};

export default systemsSlice.reducer;
